/* ---------------------------------------------------------------------------
   The baseline as a Gantt — the modeller's own, borrowed.

   The register had two ways to draw a programme as bars: write one, or use the
   Primavera-style view the modeller already has in
   vendor/bridge-modeler/src/views/ScheduleView.ts — a synchronised activity
   table and time axis, WBS collapse, float and duration columns, a week /
   month / quarter scale, fit-to-window and a column chooser, 1,266 lines of it,
   already used against these very activities. Writing a second one would have
   produced a worse Gantt and a second thing to keep working.

   So this file is the adapter, and it is deliberately thin. What the plan is
   comes from src/data/frozenSchedule.ts; what it is measured against comes from
   src/data/variance.ts; this decides only WHERE the view goes, WHEN it is
   built, and when it is taken down again.

   ══ THREE THINGS IT HAS TO GET RIGHT ══

   **The reconciler must not touch it.** The view owns its subtree and re-renders
   it itself on a scroll, a collapse or a scale change. `data-keep` is how this
   application says that — see KEEP in src/core/dom.ts, and the two map slots in
   src/map/nepalMap.ts that already use it. The slot is drawn by the render like
   any other div; what goes inside it is put there afterwards, by `syncGantt`,
   from src/main.ts's paint tail, exactly as the 3D scene and the fly map are.

   **It must not be in the entry bundle.** The Gantt, the solver behind it and
   its stylesheet come to about 37 kB — 11 kB over the wire — and they are
   wanted by the few readers on the few structures that are under construction
   and have a frozen baseline. All three are therefore reached through a
   dynamic `import()`, which gives Rollup a chunk boundary, and the card
   reports the wait. Modest next to the fly map's megabyte, and free is still
   better than modest for everybody who never opens it.

   **It must come down when it is not wanted.** `Unmount()` removes the view's
   listener on the schedule and its DOM. Not doing so would leave a subscriber
   holding a whole baseline alive on every structure a reader walked past.

   ══ WHAT IT IS NOT ══

   AN EDITOR ONLY IN THE SCHEDULER, for those who manage the programme. There
   the view is handed src/data/draftSchedule.ts: bars drag and resize, links
   draw, and what that makes is a draft over the baseline, kept by
   src/data/programmeDraft.ts until it is proposed as a revision or discarded.
   Everywhere else — the construction report, a reader without the right — it
   is handed src/data/frozenSchedule.ts, whose mutators do nothing, and a
   dragged bar simply springs back.

   WHAT STILL WORKS, which is the other half of that and was checked rather than
   hoped for: every control that reads the plan rather than writing it calls the
   view's own `Render()` directly and never goes near the schedule. The time
   scale, fit-to-window, expand and collapse all, the WBS chevrons, the column
   chooser and both scrollbars are all live. What is inert is exactly the set
   that would change a frozen plan — the duration and start cells, dragging a
   bar, and drawing or deleting a dependency.

   ONE SHARED KEY, AND IT IS BENIGN. The view remembers its collapsed rows,
   scale and hidden columns in `localStorage` under a name of the modeller's
   choosing, so a reader who collapses a group here finds it collapsed in the
   modeller and the other way about. Left alone: the values are a handful of
   strings, both the read and the write are already inside try/catch upstream,
   and one preference for one Gantt is the better behaviour anyway.

   NOR IS IT THE VARIANCE. The bars are the plan. What actually happened is the
   table under it, in src/screens/programme.ts, and the two are drawn together
   rather than merged: the modeller's Activity has no actuals — no actual start,
   no actual finish, no percent complete — which is the finding CONSTRUCTION.md
   §2 turns on. Painting an actuals bar into a view whose data model has no
   place for one would mean forking the vendored file, and we do not fork it.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions } from '../core/store.ts';
import type { Baseline } from '../data/variance.ts';
import type { WorkCalendar } from '../data/calendar.ts';
import type { DraftSchedule } from '../data/draftSchedule.ts';
import { clearDraft, onDraftCleared, readDraft, writeDraft } from '../data/programmeDraft.ts';

/* The two vendored modules, by the aliases scripts/vite-plugin-modeler.ts
   resolves and src/modeler-host/vendor.d.ts declares. Imported for their types
   only — `import type` emits nothing, so the entry bundle stays clear of them
   and the real import below is the dynamic one. */
import type { ScheduleView } from '@modeler/schedule-view';

/* --------------------------- what is on screen --------------------------- */

/** How the load is going, for the card to draw. */
export type GanttStatus =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready' }
  | { state: 'failed'; message: string };

let status: GanttStatus = { state: 'idle' };
let notify: (() => void) | null = null;

export function ganttStatus(): GanttStatus {
  return status;
}

/** Repaint when the load finishes — it is asynchronous and outside the store. */
export function wireGanttStatus(cb: () => void): void {
  notify = cb;
}

function setStatus(next: GanttStatus): void {
  status = next;
  notify?.();
}

/* --------------------------- the slot --------------------------- */

/** The name on the `data-keep` marker, and the class the stylesheet sizes. */
const SLOT = 'gantt';

/**
 * The box the view is mounted into.
 *
 * Drawn by the ordinary render so that it is laid out, measured and themed
 * like everything else on the card; filled by `syncGantt` afterwards. Its
 * height is fixed in CSS rather than grown to fit the activities — a baseline
 * on a real structure runs to a few hundred rows, and a card that tall would
 * take over the record. The view scrolls inside it, which is what it was built
 * to do.
 */
export function ganttSlot(): HTMLElement {
  return h('div', { class: 'gantt__slot', 'data-keep': SLOT });
}

/** The name on the header's slot, which the view's toolbar is moved into. */
const TOOLS = 'gantt-tools';

/**
 * Where the view's toolbar goes when the Gantt is docked in a panel: in the
 * panel's header, beside its name, rather than a second bar under it. Kept
 * like the slot, and filled by `placeToolbar`. Where there is no such box —
 * the card in the report — the toolbar stays inside the view.
 */
export function ganttToolsSlot(): HTMLElement {
  return h('div', { class: 'gantt__tools', 'data-keep': TOOLS });
}

/* --------------------------- the mount --------------------------- */

/** What is currently drawn, so an unchanged baseline is not rebuilt. */
let showing: { baselineId: string; host: HTMLElement; editable: boolean } | null = null;
let view: ScheduleView | null = null;
/** The schedule the view edits, where it may. */
let draft: DraftSchedule | null = null;
let unsubDraft: (() => void) | null = null;
let saveTimer = 0;
/** How many activities and links the draft has changed, for the header. */
let changes = 0;
/** Puts the splitter back each time the view redraws itself. */
let watcher: MutationObserver | null = null;
/** Guards against two builds racing when a repaint lands mid-load. */
let building = 0;
/** The view's latest toolbar, once moved out of it into the header. */
let toolbar: HTMLElement | null = null;

function dispose(): void {
  flushDraft();
  unsubDraft?.();
  unsubDraft = null;
  draft = null;
  setChanges(0);
  watcher?.disconnect();
  watcher = null;
  toolbar?.remove();
  toolbar = null;
  view?.Unmount();
  view = null;
  showing = null;
}

/**
 * Reconcile the Gantt with state, after the render.
 *
 * Called unconditionally from paint with the baseline to draw, or null. Null
 * covers every way the view stops being wanted at once — the card folded, the
 * reader moved to another structure, the baseline superseded, the screen
 * changed — so no caller has to enumerate them.
 */
export function syncGantt(
  wanted: Baseline | null,
  tableWidth: number | null = null,
  edit: WorkCalendar | null = null,
): void {
  const slot = document.querySelector<HTMLElement>(`[data-keep="${SLOT}"]`);
  if (slot) applyTableWidth(slot, tableWidth);
  /* A repaint can draw the header's box afresh — another view and back — and
     the toolbar moved into the old one went with it. */
  if (slot && view) placeToolbar(slot);

  if (!wanted || !slot) {
    if (view) dispose();
    /* Cleared even when there was nothing to dispose of, so that leaving and
       coming back is a fresh attempt rather than a remembered failure — the
       rule syncFlyMap states. */
    showing = null;
    if (status.state !== 'idle') setStatus({ state: 'idle' });
    return;
  }

  /* Same baseline in the same box: leave it alone. Keyed on the baseline's id
     and the element, because a repaint for anything else — a match confirmed,
     a scale changed, an entry verified — must not throw away the reader's
     scroll position and collapsed rows. */
  const editable = edit !== null;
  if (showing?.baselineId === wanted.id && showing.host === slot && showing.editable === editable) return;

  if (view) dispose();
  showing = { baselineId: wanted.id, host: slot, editable };

  const token = ++building;
  setStatus({ state: 'loading' });

  void build(slot, wanted, edit, token).catch((err: unknown) => {
    if (token !== building) return;
    /* `showing` is left set, for the reason the fly map leaves it set: clearing
       it would make the repaint this very status change causes see an empty
       slot and start the build again, forever. */
    setStatus({
      state: 'failed',
      message:
        err instanceof Error
          ? err.message
          : 'The programme view could not be loaded.',
    });
  });
}

async function build(slot: HTMLElement, baseline: Baseline, edit: WorkCalendar | null, token: number): Promise<void> {
  /* THE DYNAMIC IMPORTS, and the reason this function is async at all. All
     three land in one chunk, because the first two share the vendored tree and
     the third is that tree's stylesheet.

     THE STYLESHEET IS IMPORTED HERE RATHER THAN BESIDE THE OTHERS IN main.ts,
     and that is the point of importing it at all: `@modeler/schedule.css` is a
     virtual module that scripts/vite-plugin-modeler.ts cuts out of the
     modeller's own sheet, and pulling it in on this path means its ~720 rules
     arrive with the view and not with the entry bundle. src/styles/gantt.css
     holds the handful that have to be there from the start — the slot's size,
     and the colour translation the rules below it read. */
  const [{ FrozenSchedule }, { DraftSchedule: Draft }, { ScheduleView: View }] = await Promise.all([
    import('../data/frozenSchedule.ts'),
    import('../data/draftSchedule.ts'),
    import('@modeler/schedule-view'),
    import('@modeler/schedule.css'),
  ]);

  /* A slower load that has been superseded — the reader moved on while the
     chunk was in flight — stops here rather than mounting over whatever is
     there now. */
  if (token !== building) return;

  const editing = edit === null ? null : new Draft(baseline, edit, readDraft(baseline.id));
  const schedule = editing ?? new FrozenSchedule(baseline);

  /* NEITHER CALLBACK IS PASSED, which is what keeps "Simulate" and "Recreate"
     off the toolbar: the view draws each button only where it was given
     something to call. Simulate needs the modeller's 3D viewport, which this
     page does not have, and Recreate rebuilds a schedule from the bridge tree,
     which is a planning act on a plan this register may not touch. */
  const mounted = new View(schedule, slot);
  mounted.Mount();

  view = mounted;
  if (editing) {
    draft = editing;
    setChanges(editing.Changes());
    unsubDraft = editing.Subscribe(() => {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(flushDraft, 250);
    });
  }
  watchForSplitter(slot);
  setStatus({ state: 'ready' });
}

/* --------------------------- the draft --------------------------- */

function setChanges(n: number): void {
  if (n === changes) return;
  changes = n;
  notify?.();
}

/** Write the draft out, or drop it once every edit has been undone by hand. */
function flushDraft(): void {
  window.clearTimeout(saveTimer);
  saveTimer = 0;
  if (!draft) return;
  const n = draft.Changes();
  if (n > 0) writeDraft(draft.Inputs());
  else clearDraft(draft.baselineId);
  setChanges(n);
}

/** How many activities and links the Gantt's draft has changed; 0 with none. */
export function ganttDraftChanges(): number {
  return changes;
}

/** Throw the draft away and draw the baseline again. */
export function discardGanttDraft(): void {
  if (draft) clearDraft(draft.baselineId);
}

/* A draft cleared anywhere — discarded here, or proposed from the store — is
   the baseline again, so the view is rebuilt from it. */
onDraftCleared((baselineId) => {
  if (!draft || draft.baselineId !== baselineId || draft.Changes() === 0) return;
  /* Let go of it first, so that taking the view down does not save it again. */
  window.clearTimeout(saveTimer);
  draft = null;
  dispose();
  showing = null;
  notify?.();
});

/* THE FINISH CELL. The view turns a typed finish into a duration by counting
   calendar days from the start, which is a day short against a finish that is
   the last day of work, and wrong on any calendar with rest days. Caught on the
   way down and answered by the draft instead. */
function onFinishTyped(e: Event): void {
  const input = e.target as HTMLInputElement | null;
  if (!draft || !input?.matches('.sv-col-finish input[type="date"]') || !input.value) return;
  const id = input.closest<HTMLElement>('.sv-tr')?.dataset['activityId'];
  if (!id) return;
  e.stopPropagation();
  draft.SetFinish(id, input.value);
}

/* --------------------------- the splitter --------------------------- */

/* The vendored sheet makes the table pane `resize: horizontal`, which gives a
   10px grip in its bottom corner that few find — and the width it leaves is
   thrown away on the next redraw, because the view rebuilds its panes on every
   scroll, collapse and scale change. So the width is the slot's `--sv-table-w`
   (src/styles/gantt.css), which outlives a redraw, and the handle is put back
   into the panes each time they are rebuilt. The view itself is not touched. */

const MIN_TABLE_W = 240;
/** What the bars keep, however far the table is pulled. */
const MIN_BARS_W = 160;
const KEY_STEP = 16;

function applyTableWidth(slot: HTMLElement, px: number | null): void {
  if (px === null) {
    slot.style.removeProperty('--sv-table-w');
    slot.classList.remove('gantt__slot--sized');
  } else {
    slot.style.setProperty('--sv-table-w', `${px}px`);
    slot.classList.add('gantt__slot--sized');
  }
}

function watchForSplitter(slot: HTMLElement): void {
  const root = slot.querySelector<HTMLElement>('.schedule-view');
  if (!root) return;
  watcher?.disconnect();
  watcher = new MutationObserver(() => {
    placeSplitter(slot);
    placeToolbar(slot);
  });
  watcher.observe(root, { childList: true });
  root.addEventListener('change', onFinishTyped, true);
  placeSplitter(slot);
  placeToolbar(slot);
}

/* THE TOOLBAR, IN THE PANEL'S HEADER. The view rebuilds its toolbar with its
   panes on every redraw, so the fresh one is moved out each time and the one
   it replaces dropped — the same trick as the splitter, and again the view
   itself is not touched. Nothing in it looks the toolbar up under its root. */
function placeToolbar(slot: HTMLElement): void {
  const host = document.querySelector<HTMLElement>(`[data-keep="${TOOLS}"]`);
  if (!host) return;
  const fresh = slot.querySelector<HTMLElement>('.schedule-view > .sv-toolbar');
  if (fresh) {
    toolbar?.remove();
    toolbar = fresh;
  }
  if (toolbar && toolbar.parentElement !== host) host.replaceChildren(toolbar);
}

/** How far the table is scrolled sideways, carried across the view's redraws —
 *  the view restores its own vertical scroll but knows nothing of this one. */
let tableScrollLeft = 0;

function placeSplitter(slot: HTMLElement): void {
  const pane = slot.querySelector<HTMLElement>('.sv-panes > .sv-table-pane');
  if (!pane || pane.nextElementSibling?.classList.contains('sv-split')) return;
  pane.after(splitter(slot));

  /* The header does not scroll by itself (the vendored sheet clips it), so it
     is moved with the body; a too-narrow table is otherwise a header over the
     wrong columns. */
  const header = pane.querySelector<HTMLElement>('.sv-table-header');
  const body = pane.querySelector<HTMLElement>('.sv-table-body');
  if (!header || !body) return;
  body.scrollLeft = tableScrollLeft;
  header.scrollLeft = body.scrollLeft;
  body.addEventListener('scroll', () => {
    tableScrollLeft = body.scrollLeft;
    header.scrollLeft = body.scrollLeft;
  });
}

/** The widest the table may be in this slot as it is now laid out. */
function maxTableWidth(slot: HTMLElement): number {
  const panes = slot.querySelector<HTMLElement>('.sv-panes');
  return Math.max(MIN_TABLE_W, (panes?.clientWidth ?? 0) - MIN_BARS_W);
}

function clampWidth(slot: HTMLElement, px: number): number {
  return Math.min(maxTableWidth(slot), Math.max(MIN_TABLE_W, px));
}

function currentWidth(slot: HTMLElement): number {
  return slot.querySelector<HTMLElement>('.sv-table-pane')?.getBoundingClientRect().width ?? MIN_TABLE_W;
}

/**
 * Drag it, double-click it to give the columns back the width they ask for, or
 * focus it and use the arrow keys — the same handle as the record's own
 * splitter in src/screens/detail.ts. Written straight onto the slot while the
 * drag is on and committed to the store once, when it ends.
 */
function splitter(slot: HTMLElement): HTMLElement {
  return h('div', {
    class: 'sv-split',
    role: 'separator',
    tabindex: '0',
    'aria-orientation': 'vertical',
    'aria-label': 'Resize the activity table',
    title: 'Drag to resize · double-click to reset',
    onpointerdown: (e: PointerEvent) => beginResize(e, slot),
    ondblclick: () => actions.setGanttTableWidth(null),
    onkeydown: (e: KeyboardEvent) => {
      const step = e.shiftKey ? KEY_STEP * 4 : KEY_STEP;
      const w = currentWidth(slot);
      const to =
        e.key === 'ArrowLeft'
          ? w - step
          : e.key === 'ArrowRight'
            ? w + step
            : e.key === 'Home'
              ? MIN_TABLE_W
              : e.key === 'End'
                ? maxTableWidth(slot)
                : null;
      if (to === null) return;
      e.preventDefault();
      actions.setGanttTableWidth(clampWidth(slot, to));
    },
  });
}

function beginResize(e: PointerEvent, slot: HTMLElement): void {
  if (e.button !== 0) return;
  e.preventDefault();
  const handle = e.currentTarget as HTMLElement;
  const startX = e.clientX;
  const startWidth = currentWidth(slot);
  let w = startWidth;

  document.body.classList.add('is-col-resizing');
  handle.classList.add('is-dragging');

  const onMove = (ev: PointerEvent): void => {
    w = clampWidth(slot, startWidth + (ev.clientX - startX));
    applyTableWidth(slot, w);
  };
  const onUp = (): void => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    document.body.classList.remove('is-col-resizing');
    handle.classList.remove('is-dragging');
    if (w !== startWidth) actions.setGanttTableWidth(w);
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
}
